import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Tooltip,
  ClickAwayListener
} from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { AuthContext } from '../context/AuthContext';
import Layout from '../components/Layout';
import { colors } from '../styles/styleConstants';
import itemService from '../services/itemService';

const ITEM_THRESHOLD = 20;
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

const SimpleHome = () => {
  const navigate = useAppNavigation();
  const { currentUser } = useContext(AuthContext);

  const [itemCount, setItemCount] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const evaluateNudge = async () => {
      try {
        const snoozedUntil = parseInt(
          localStorage.getItem('clutterlogix_simple_nudge_snoozed_until') || '0',
          10
        );
        if (snoozedUntil > Date.now()) return;

        const items = await itemService.getAllItems({ limit: 10000 });
        if (cancelled) return;
        const count = Array.isArray(items) ? items.length : 0;
        setItemCount(count);
        if (count > ITEM_THRESHOLD) {
          setAutoOpen(true);
        }
      } catch (e) {
        // Non-fatal — just don't show the nudge.
      }
    };
    evaluateNudge();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismissNudge = () => {
    localStorage.setItem(
      'clutterlogix_simple_nudge_snoozed_until',
      String(Date.now() + SNOOZE_MS)
    );
    setAutoOpen(false);
    setHoverOpen(false);
  };

  const handleSwitchToFull = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem('clutterlogix_home_mode', 'legacy');
    localStorage.setItem('clutterlogix_switched_to_legacy_at', String(Date.now()));
    localStorage.setItem('clutterlogix_legacy_active_days', JSON.stringify([today]));
    localStorage.setItem(
      'clutterlogix_simple_nudge_snoozed_until',
      String(Date.now() + SNOOZE_MS)
    );
    window.location.reload();
  };

  const handleAddItem = () => navigate.to.ADD_ITEM();
  const handleFindItems = () => navigate.to.FIND();
  const handleAddBox = () => navigate.to.CREATE_BOX();

  const tooltipOpen = autoOpen || hoverOpen;
  const highlightSwitch = autoOpen;

  const nudgeContent = (
    <Box sx={{ p: 0.5, maxWidth: 260 }}>
      <Typography
        variant="body2"
        sx={{ mb: 1, color: 'inherit', lineHeight: 1.4 }}
      >
        You have {itemCount ?? `${ITEM_THRESHOLD}+`} items. The Full Dashboard
        adds stats, recent activity, and quick filters. You can switch back to
        Simple anytime.
      </Typography>
      <Button
        size="small"
        variant="text"
        onClick={(e) => {
          e.stopPropagation();
          dismissNudge();
        }}
        sx={{
          color: 'inherit',
          textTransform: 'none',
          fontSize: '0.75rem',
          px: 0,
          minWidth: 'auto',
          textDecoration: 'underline'
        }}
      >
        Got it
      </Button>
    </Box>
  );

  return (
    <Layout title="ClutterLogix" disableBackButton={true}>
      <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: colors.slateBlue, mb: 1 }}>
            Welcome{currentUser?.name ? `, ${currentUser.name}` : ''}
          </Typography>
          <Typography variant="body1" sx={{ color: colors.mediumSlateBlue, mb: 4 }}>
            Let's get organized. What would you like to do?
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleAddItem}
            sx={{
              py: 3,
              backgroundColor: colors.actionBlue,
              color: colors.softWhite,
              borderRadius: 3,
              fontSize: '1.25rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <AddIcon sx={{ fontSize: 40, mr: 2 }} />
            Add Item
          </Button>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleFindItems}
            sx={{
              py: 3,
              backgroundColor: '#10b981',
              color: colors.softWhite,
              borderRadius: 3,
              fontSize: '1.25rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
              '&:hover': {
                backgroundColor: '#059669',
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <SearchIcon sx={{ fontSize: 40, mr: 2 }} />
            Find Items
          </Button>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleAddBox}
            sx={{
              py: 3,
              backgroundColor: '#8b5cf6',
              color: colors.softWhite,
              borderRadius: 3,
              fontSize: '1.25rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
              '&:hover': {
                backgroundColor: '#7c3aed',
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 20px rgba(139, 92, 246, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <InventoryIcon sx={{ fontSize: 40, mr: 2 }} />
            Add Box
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: colors.mediumSlateBlue, fontStyle: 'italic' }}>
            💡 Tip: Create a box first, then add items to it for easy organization!
          </Typography>
        </Box>

        {/* Switch to Full Dashboard */}
        <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: `1px solid ${colors.lightGray}` }}>
          <ClickAwayListener
            onClickAway={() => {
              if (autoOpen) dismissNudge();
            }}
          >
            <Tooltip
              open={tooltipOpen}
              onOpen={() => setHoverOpen(true)}
              onClose={() => setHoverOpen(false)}
              title={nudgeContent}
              placement="top"
              arrow
              disableFocusListener
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: colors.slateBlue,
                    color: colors.softWhite,
                    fontSize: '0.875rem',
                    maxWidth: 280,
                    p: 1.25
                  }
                },
                arrow: { sx: { color: colors.slateBlue } }
              }}
            >
              <Button
                variant={highlightSwitch ? 'contained' : 'text'}
                size="small"
                startIcon={<DashboardIcon />}
                onClick={handleSwitchToFull}
                sx={{
                  color: highlightSwitch ? colors.softWhite : colors.mediumSlateBlue,
                  backgroundColor: highlightSwitch ? colors.actionBlue : 'transparent',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: highlightSwitch ? 600 : 400,
                  animation: highlightSwitch
                    ? 'simple-home-switch-pulse 2.2s ease-in-out infinite'
                    : 'none',
                  '@keyframes simple-home-switch-pulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.55)' },
                    '50%': { boxShadow: '0 0 0 10px rgba(59, 130, 246, 0)' }
                  },
                  '&:hover': {
                    backgroundColor: highlightSwitch
                      ? colors.actionBlue
                      : 'transparent'
                  }
                }}
              >
                Switch to Full Dashboard
              </Button>
            </Tooltip>
          </ClickAwayListener>
        </Box>
      </Container>
    </Layout>
  );
};

export default SimpleHome;
